/**
 * Stats → Overview (A7Y-5020).
 *
 * Three independently cached blocks (`/admin/stats/overview/<block>`) so one slow query never holds the
 * others: listing history, historical icon/screenshot assets and keyword data-store counts. Refresh
 * re-fetches every block with `force`, which re-counts the live lines; the per-app, join and asset figures
 * come from a nightly snapshot that Refresh reloads and cannot recompute, and the line beside the control
 * says so with the snapshot's date.
 */
import { useRef, useState, type MutableRefObject } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import type {
  StatsOverviewAssets,
  StatsOverviewBlockName,
  StatsOverviewKeywords,
  StatsOverviewListingHistory,
} from '@a7y/types';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { formatUtcInstant } from './format';
import { AssetsBlock } from './overview/AssetsBlock';
import { KeywordsBlock } from './overview/KeywordsBlock';
import { ListingHistoryBlock } from './overview/ListingHistoryBlock';
import { OverviewBlockPanel } from './overview/OverviewBlockPanel';

type ForceRef = MutableRefObject<boolean>;

/** One block's query; `forceRef` is read at fetch time so Refresh can force exactly one round. */
function useOverviewBlock<T>(name: StatsOverviewBlockName, forceRef: ForceRef) {
  return useQuery({
    queryKey: ['stats-overview', name],
    queryFn: async () => (await api.stats.overview.block<T>(name, { force: forceRef.current })).data,
  });
}

export function StatsOverviewPage() {
  const queryClient = useQueryClient();
  const forceRef = useRef(false);
  const [refreshing, setRefreshing] = useState(false);

  const listingQ = useOverviewBlock<StatsOverviewListingHistory>('listing-history', forceRef);
  const assetsQ = useOverviewBlock<StatsOverviewAssets>('assets', forceRef);
  const keywordsQ = useOverviewBlock<StatsOverviewKeywords>('keywords', forceRef);

  const refresh = async () => {
    forceRef.current = true;
    setRefreshing(true);
    try {
      await queryClient.refetchQueries({ queryKey: ['stats-overview'], type: 'active' });
    } finally {
      forceRef.current = false;
      setRefreshing(false);
    }
  };

  const listing = listingQ.data;
  const assets = assetsQ.data;
  const snapshotComputedAt =
    (listing?.state === 'ok' ? listing.body.snapshot?.computedAt : undefined) ??
    (assets?.state === 'ok' ? assets.body.computedAt : undefined);

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Stats Overview"
        subtitle="Production data-store counts: listing history, historical icons and screenshots, and the keyword dataset."
      >
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={refreshing}
          className="inline-flex items-center gap-1 rounded-md border border-zinc-200 dark:border-zinc-700 px-2 py-1 text-xs text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </PageHeader>
      <p data-testid="refresh-note" className="-mt-4 mb-6 px-2 text-xs text-zinc-500 dark:text-zinc-400">
        Refresh re-counts the live lines; per-app, join and asset figures come from the nightly snapshot of{' '}
        {snapshotComputedAt ? formatUtcInstant(snapshotComputedAt) : '(none read yet)'} and are reloaded, not
        recomputed.
      </p>

      <OverviewBlockPanel title="Listing history" testId="block-listing-history" query={listingQ}>
        {(body, block) => <ListingHistoryBlock body={body} meta={block} />}
      </OverviewBlockPanel>
      <OverviewBlockPanel title="Historical icons and screenshots" testId="block-assets" query={assetsQ}>
        {(body) => <AssetsBlock body={body} />}
      </OverviewBlockPanel>
      <OverviewBlockPanel title="Keywords" testId="block-keywords" query={keywordsQ}>
        {(body, block) => <KeywordsBlock body={body} meta={block} />}
      </OverviewBlockPanel>
    </div>
  );
}
